import { gql, useApolloClient } from "@apollo/client";
import {
  Button,
  Flex,
  Heading,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import React, { useCallback, useMemo, useState } from "react";
import { RiEditLine, RiRoadMapLine } from "react-icons/ri";
import { SchoolBoundaries } from "src/components/Boundary/schemas";
import { Card } from "src/components/Layout/Card";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import { Glossary } from "src/components/Text/Glossary";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { GET_SCHOOL_DEFAULT_BIN_CONFIG } from "src/scenes/orgAdmin/match/graphql/queries";
import { isNotNull } from "src/services/predicates";
import * as GQL from "src/types/graphql";
import {
  BULK_INSERT_GRADES_GROUPS,
  DELETE_BIN_BY_IDS,
  DELETE_GRADES_GROUPS,
} from "../graphql/mutations";
import { GET_GRADES_GROUPS_BY_ENROLLMENT_SCHOOL } from "../graphql/queries";
import {
  GradesGroup,
  GradesGroupLists,
  gradesGroupToGradePrograms,
  gradesGroupToLists,
  listsToGradesGroupInsert,
  OrderedItem,
  orderedItemSortFn,
} from "../helpers";
import { SetBoundaryConnectionsDrawer } from "../SetBoundaryConnectionsDrawer";
import { BoundaryInfo } from "./BoundaryInfo";
import { GradesGroupCard, GradesGroupText } from "./GradesGroupCard";

type Mode = "View" | "Edit";

interface SchoolGradeProgramsCardProps {
  organizationId: string;
  schoolId: string;
  schoolName: string;
  enrollmentPeriodId: string;
  gradeConfigs: GQL.GetEnrollmentSchoolsAndConfigs_grade_config[];
  grades: GQL.GetEnrollmentSchoolsAndConfigs_school_grades[];
  programConfigs: GQL.GetEnrollmentSchoolsAndConfigs_program_group_programs[];
  boundaryMap: Immutable.Map<string, SchoolBoundaries>;
  refetchBoundaryMap?: () => Promise<void>;
}

export const SchoolGradeProgramsCard: React.FC<
  SchoolGradeProgramsCardProps
> = ({
  organizationId,
  schoolId,
  schoolName,
  enrollmentPeriodId,
  gradeConfigs,
  grades,
  programConfigs,
  boundaryMap,
  refetchBoundaryMap,
}) => {
  const toast = useToast();
  const [mode, setMode] = useState<Mode>("View");
  const flags = useFlags(["boundary-management"]);
  const {
    isOpen: isBoundariesDrawerOpen,
    onOpen: onBoundariesDrawerOpen,
    onClose: onBoundariesDrawerClose,
  } = useDisclosure();

  const [initialGradesGroups, setInitialGradesGroups] =
    useState<GradesGroup[]>();
  const [gradesGroups, setGradesGroups] = useState<GradesGroupLists[]>([]);

  const { remoteData, refetch } = useRemoteDataQuery<
    GQL.GetGradesGroupsByEnrollmentSchool,
    GQL.GetGradesGroupsByEnrollmentSchoolVariables
  >(GET_GRADES_GROUPS_BY_ENROLLMENT_SCHOOL, {
    variables: {
      enrollment_period_id: enrollmentPeriodId,
      school_id: schoolId,
    },
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const groups: GradesGroup[] = data.grades_group.map((gradesGroup) => ({
        id: gradesGroup.id,
        lists: gradesGroupToLists(gradesGroup),
        gradePrograms: gradesGroupToGradePrograms(gradesGroup),
      }));
      setInitialGradesGroups(groups);
      setGradesGroups(
        groups && groups.length > 0
          ? groups.map((group) => group.lists)
          : [{ grades: [], programs: [] }]
      );
    },
  });

  const reset = useCallback(async () => {
    await refetch();
    setMode("View");
  }, [refetch]);

  const apolloClient = useApolloClient();

  const [bulkInsertGradesGroups, { remoteData: updateGradesGroups }] =
    useRemoteDataMutation<
      GQL.BulkInsertGradesGroups,
      GQL.BulkInsertGradesGroupsVariables
    >(BULK_INSERT_GRADES_GROUPS);

  const [deleteGradesGroups, { remoteData: deletedGradesGroups }] =
    useRemoteDataMutation<
      GQL.DeleteGradesGroups,
      GQL.DeleteGradesGroupsVariables
    >(DELETE_GRADES_GROUPS);

  const [deleteBinByIds, { remoteData: deletedBinByIds }] =
    useRemoteDataMutation<GQL.DeleteBinByIds, GQL.DeleteBinByIdsVariables>(
      DELETE_BIN_BY_IDS
    );

  const isLoading =
    remoteData.isLoading() ||
    updateGradesGroups.isLoading() ||
    deletedGradesGroups.isLoading() ||
    deletedBinByIds.isLoading();

  const handleSubmit = useCallback(async () => {
    if (gradesGroups.some((gradesGroup) => gradesGroup.grades.length === 0)) {
      toast({
        id: "update-grades-groups-error",
        status: "error",
        title: "Error updating grades groups",
        description: "Grades cannot be empty.",
        position: "bottom-right",
      });
      return;
    }
    if (gradesGroups.some((gradesGroup) => gradesGroup.programs.length === 0)) {
      toast({
        id: "update-grades-groups-error",
        status: "error",
        title: "Error updating grades groups",
        description: "Programs cannot be empty.",
        position: "bottom-right",
      });
      return;
    }

    const { data: schoolDefaultBinConfigData } = await apolloClient.query<
      GQL.GetSchoolDefaultBinConfig,
      GQL.GetSchoolDefaultBinConfigVariables
    >({
      query: GET_SCHOOL_DEFAULT_BIN_CONFIG,
      variables: { schoolId, enrollmentPeriodId },
    });

    const formattedNewGradesGroups: GQL.grades_group_insert_input[] =
      gradesGroups.map((gradesGroup) =>
        listsToGradesGroupInsert({
          lists: gradesGroup,
          enrollmentPeriodId,
          schoolId,
          schoolDefaultBinConfigId:
            schoolDefaultBinConfigData.school_default_bin_config[0]
              ?.bin_config_id ?? null,
        })
      );

    try {
      if (formattedNewGradesGroups.length) {
        await bulkInsertGradesGroups({
          variables: {
            grades_groups: formattedNewGradesGroups,
          },
        });
      }
      if (initialGradesGroups && initialGradesGroups.length > 0) {
        const deletedGradesGroupIds = initialGradesGroups.map(
          (gradesGroup) => gradesGroup.id
        );

        const { data: gradesToDeleteData } = await apolloClient.query<
          GQL.GetGradesToDeleteForSchoolGradeProgramsCard,
          GQL.GetGradesToDeleteForSchoolGradeProgramsCardVariables
        >({
          query: gql`
            query GetGradesToDeleteForSchoolGradeProgramsCard(
              $grades_group_ids: [uuid!]!
            ) {
              grade(where: { grades_group_id: { _in: $grades_group_ids } }) {
                id
                bin_id
              }
            }
          `,
          variables: { grades_group_ids: deletedGradesGroupIds },
        });

        const gradeBinIdsToDelete = gradesToDeleteData.grade
          .map((grade) => grade.bin_id)
          .filter(isNotNull);

        await deleteGradesGroups({
          variables: {
            grades_group_ids: deletedGradesGroupIds,
          },
        });

        if (gradeBinIdsToDelete.length > 0) {
          try {
            await deleteBinByIds({
              variables: {
                bin_ids: gradeBinIdsToDelete,
              },
            });
          } catch (err) {
            // ignore error: bin may still be linked to other grades
          }
        }
      }

      await reset();
      toast({
        id: "update-grades-groups-success",
        title: "School updated",
        isClosable: true,
        status: "success",
        position: "bottom-right",
      });
    } catch (err) {
      console.error(err);
      toast({
        id: "update-grades-groups-error",
        title: "Error updating grades groups",
        description:
          "Please try again later or report the problem to our support team.",
        isClosable: true,
        status: "error",
      });
    }
  }, [
    apolloClient,
    bulkInsertGradesGroups,
    deleteBinByIds,
    deleteGradesGroups,
    enrollmentPeriodId,
    gradesGroups,
    initialGradesGroups,
    reset,
    schoolId,
    toast,
  ]);

  const gradeOptionsByGroup = useMemo(
    () => gradeOptionsByGroupIndex(gradeConfigs, gradesGroups),
    [gradeConfigs, gradesGroups]
  );

  const GradesGroupForms = gradesGroups.map((gradesGroup, index) => (
    <GradesGroupCard
      gradeValues={gradesGroup.grades}
      gradeOptions={gradeOptionsByGroup[index] ?? []}
      onChangeGrades={(grades: OrderedItem[]) => {
        const groups = [...gradesGroups];
        groups.splice(index, 1, {
          grades: grades.sort(orderedItemSortFn),
          programs: gradesGroup.programs,
        });
        setGradesGroups(groups);
      }}
      onRemoveGrade={(deletedGrade: OrderedItem) => {
        const groups = [...gradesGroups];
        groups.splice(index, 1, {
          grades: gradesGroup.grades.filter(
            (grade) => grade.id !== deletedGrade.id
          ),
          programs: gradesGroup.programs,
        });
        setGradesGroups(groups);
      }}
      programValues={gradesGroup.programs}
      programOptions={programConfigs}
      onChangePrograms={(programs: OrderedItem[]) => {
        const groups = [...gradesGroups];
        groups.splice(index, 1, {
          grades: gradesGroup.grades,
          programs: programs.sort(orderedItemSortFn),
        });
        setGradesGroups(groups);
      }}
      onDelete={() => {
        const groups = [...gradesGroups];
        groups.splice(index, 1);
        setGradesGroups(groups);
      }}
      key={index}
    />
  ));

  return (
    <Card
      display="flex"
      flexDirection="column"
      showBorder
      gap={4}
      padding={6}
      width="100%"
    >
      <Flex justifyContent="space-between">
        <Heading fontSize="lg" mb={4}>
          <Text as="span" color="blackAlpha.800">
            {schoolName}{" "}
          </Text>
          <Text as="span" color="gray.500">
            | {schoolId}
          </Text>
        </Heading>
        <Flex gap={2}>
          {mode === "View" && (
            <Button
              colorScheme="gray"
              variant="outline"
              leftIcon={<RiEditLine />}
              onClick={() => setMode("Edit")}
            >
              <Glossary>Edit school</Glossary>
            </Button>
          )}
          {mode === "Edit" && flags["boundary-management"].enabled && (
            <Button
              leftIcon={<RiRoadMapLine />}
              colorScheme="gray"
              variant="outline"
              onClick={onBoundariesDrawerOpen}
            >
              Set boundaries
            </Button>
          )}
        </Flex>
      </Flex>
      {isLoading ? (
        <Spinner color="primary.500" />
      ) : mode === "View" ? (
        <Flex direction="column" gap={2}>
          <Text fontSize="xs" color="gray.800">
            Grades
          </Text>
          {(!initialGradesGroups || initialGradesGroups.length === 0) && (
            <Text as="i" color="blackAlpha.700">
              No grades
            </Text>
          )}
          {initialGradesGroups?.map(({ id, lists }) => (
            <GradesGroupText {...lists} key={id} />
          ))}
          {flags["boundary-management"].enabled && (
            <BoundaryInfo
              boundaryMap={boundaryMap}
              grades={grades}
              schoolId={schoolId}
            />
          )}
        </Flex>
      ) : (
        <>
          {GradesGroupForms}
          <Button
            aria-label="Add grades group"
            width="fit-content"
            colorScheme="gray"
            onClick={() =>
              setGradesGroups([...gradesGroups, { grades: [], programs: [] }])
            }
          >
            Add grades group
          </Button>
          <Flex gap="3" justifyContent="right" paddingY="3">
            <Button
              variant="ghost"
              colorScheme="gray"
              onClick={() => {
                reset();
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              <Glossary>Update school</Glossary>
            </Button>
          </Flex>
        </>
      )}

      <SetBoundaryConnectionsDrawer
        organizationId={organizationId}
        isOpen={isBoundariesDrawerOpen}
        onClose={onBoundariesDrawerClose}
        schoolId={schoolId}
        schoolName={schoolName}
        enrollmentPeriodId={enrollmentPeriodId}
        grades={grades}
        refetchBoundaryMap={refetchBoundaryMap}
      />
    </Card>
  );
};

function gradeOptionsByGroupIndex(
  gradeConfigs: GQL.GetEnrollmentSchoolsAndConfigs_grade_config[],
  groups: GradesGroupLists[]
): OrderedItem[][] {
  const ownerByGradeId = new Map<string, number>();
  groups.forEach((group, groupIndex) => {
    for (const grade of group.grades) {
      ownerByGradeId.set(grade.id, groupIndex);
    }
  });

  return groups.map((_, groupIndex) =>
    gradeConfigs.filter((grade) => {
      const owner = ownerByGradeId.get(grade.id);
      return owner === undefined || owner === groupIndex;
    })
  );
}
